import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useDeferredValue,
} from 'react';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Link as RouterLink } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import logo from '../../pacePackPrime.png';
import {
  Box,
  Paper,
  Stack,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  ButtonBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Chip,
  Link,
  Skeleton,
  Typography,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  CheckCircleOutlined as VerifiedIcon,
  ScheduleOutlined as OutstandingIcon,
  RemoveCircleOutlineOutlined as UnpaidIcon,
  PaymentsOutlined as ValueIcon,
} from '@mui/icons-material';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

// Statuses with nothing left to collect. These read as "unpaid" because no
// payment was ever recorded against them, which is true but useless here — they
// padded the unpaid count and the outstanding value with money that was never
// owed. Note the single-l "canceled": that is the status value the app writes
// (see utils/reportGenerator.js), even though "cancelled" appears elsewhere in
// variable and field names.
const NON_RECONCILABLE_STATUSES = new Set([
  'returned',
  'canceled',
  'returning-to-store',
  'left-store',
  'in-store',
  'pending',
]);
const isReconcilable = (pkg) =>
  !NON_RECONCILABLE_STATUSES.has((pkg.status || '').toLowerCase());

// Same flexible date parsing the payments collection needs elsewhere: values come
// back as a Firestore Timestamp, a raw {seconds} object, or a legacy string.
const parseAnyDate = (raw) => {
  if (!raw) return null;
  if (raw.toDate) return raw.toDate();
  if (raw.seconds) return new Date(raw.seconds * 1000);
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
  if (typeof raw === 'string') {
    const cleaned = raw
      .replace(/ at /i, ' ')
      .replace(/ utc-4/i, ' GMT-0400')
      .replace(/ utc/i, ' GMT');
    let d = new Date(cleaned);
    if (isNaN(d.getTime())) d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

// The app displays money as TT$ (see PackageDetail). Group and pad manually rather
// than leaning on a TTD currency locale so the output matches the rest of the app.
const numberFormat = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatCurrency = (amount) => `TT$ ${numberFormat.format(amount || 0)}`;

const formatDateTime = (date) =>
  date
    ? date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

const formatDateOnly = (date) =>
  date ? date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '';

// Status carries an icon as well as a colour so it does not read as colour alone,
// and a rank so the Status column can sort in severity order.
const STATUS_META = {
  verified: {
    label: 'Verified',
    rank: 0,
    Icon: VerifiedIcon,
    sx: { bgcolor: '#dcfce7', color: '#166534', '& .MuiChip-icon': { color: '#166534' } },
  },
  outstanding: {
    label: 'Outstanding',
    rank: 1,
    Icon: OutstandingIcon,
    sx: { bgcolor: '#fef3c7', color: '#92400e', '& .MuiChip-icon': { color: '#92400e' } },
  },
  unpaid: {
    label: 'Unpaid',
    rank: 2,
    Icon: UnpaidIcon,
    sx: { bgcolor: '#f1f5f9', color: '#334155', '& .MuiChip-icon': { color: '#475569' } },
  },
};

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status];
  const { Icon } = meta;
  return (
    <Chip
      size="small"
      icon={<Icon sx={{ fontSize: 16 }} />}
      label={meta.label}
      sx={{ fontWeight: 600, ...meta.sx }}
    />
  );
};

// A stat card doubles as a filter toggle when onClick is supplied. Kept as a real
// button so it is keyboard reachable and announces its pressed state.
const StatCard = ({ label, value, hint, accent, Icon, active, onClick, loading }) => {
  const content = (
    <Stack spacing={0.5} sx={{ width: '100%', textAlign: 'left' }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Icon sx={{ fontSize: 18, color: accent }} aria-hidden="true" />
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.2 }}>
          {label}
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton variant="text" width={72} sx={{ fontSize: '1.75rem' }} />
      ) : (
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ color: accent, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}
        >
          {value}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ minHeight: 18 }}>
        {loading ? ' ' : hint}
      </Typography>
    </Stack>
  );

  const paperSx = {
    p: { xs: 1.5, md: 2 },
    height: '100%',
    borderColor: active ? accent : 'divider',
    // Keep the box the same size when active so the grid does not shift on toggle.
    boxShadow: active ? `inset 0 0 0 1px ${accent}` : 'none',
    transition: 'border-color 160ms ease, box-shadow 160ms ease',
  };

  if (!onClick) {
    return (
      <Paper variant="outlined" sx={paperSx}>
        {content}
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ ...paperSx, p: 0, overflow: 'hidden' }}>
      <ButtonBase
        onClick={onClick}
        aria-pressed={active}
        sx={{
          width: '100%',
          height: '100%',
          p: { xs: 1.5, md: 2 },
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {content}
      </ButtonBase>
    </Paper>
  );
};

const UserPackageSummary = () => {
  const [authUser] = useAuthState(auth);

  const [packages, setPackages] = useState([]);
  const [payments, setPayments] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingId, setProcessingId] = useState(null);
  // The toast content outlives its open flag so it stays mounted through the exit
  // transition, then clears on onExited.
  const [toast, setToast] = useState(null); // { message, severity, undo }
  const [toastOpen, setToastOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | verified | outstanding | unpaid
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  // Typing stays responsive on a large package list: the input updates immediately
  // while the filtered table catches up on the deferred value.
  const deferredSearch = useDeferredValue(search);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [packagesSnap, paymentsSnap, usersSnap] = await Promise.all([
        getDocs(collection(db, 'packages')),
        getDocs(collection(db, 'payments')),
        getDocs(collection(db, 'users')),
      ]);

      setPackages(packagesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      // The payments collection also holds the receiptNumber counter document,
      // which has no packageId - skip it so it never becomes a row.
      setPayments(
        paymentsSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.packageId)
      );

      const uMap = {};
      usersSnap.docs.forEach(d => { uMap[d.id] = d.data(); });
      setUsersMap(uMap);
    } catch (err) {
      console.error('Error fetching package payment data:', err);
      setError('Could not load package payments. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authUser) fetchData();
  }, [authUser, fetchData]);

  // Resolve a uid to a person's name, falling back to the raw uid when the user
  // record has no name on it (or no longer exists).
  const displayName = useCallback((uid) => {
    if (!uid) return null;
    const u = usersMap[uid];
    return u?.name || u?.displayName || u?.email || uid;
  }, [usersMap]);

  // One payment per package: prefer the id the package itself points at, otherwise
  // the most recent payment recorded against it.
  const paymentForPackage = useMemo(() => {
    const byPackage = {};
    const byId = {};
    payments.forEach((pmt) => {
      byId[pmt.id] = pmt;
      const current = byPackage[pmt.packageId];
      if (!current) {
        byPackage[pmt.packageId] = pmt;
        return;
      }
      const a = parseAnyDate(pmt.paymentDate)?.getTime() ?? 0;
      const b = parseAnyDate(current.paymentDate)?.getTime() ?? 0;
      if (a > b) byPackage[pmt.packageId] = pmt;
    });
    return (pkg) => (pkg.paymentId && byId[pkg.paymentId]) || byPackage[pkg.id] || null;
  }, [payments]);

  // One row per package, with everything the table sorts, searches, and renders on
  // resolved once rather than per render pass.
  //
  // Returned packages are dropped outright. They are unpaid in the literal
  // sense, but there is nothing to collect on a package that went back, so
  // counting them inflated the outstanding figures this page exists to track.
  const reconcilablePackages = useMemo(() => packages.filter(isReconcilable), [packages]);

  // Surfaced in the footer so a shrinking total reads as deliberate rather than
  // as packages having gone missing.
  const excludedCount = packages.length - reconcilablePackages.length;

  const rows = useMemo(() => reconcilablePackages.map((pkg) => {
    const payment = paymentForPackage(pkg);

    let status;
    if (payment) status = payment.verified ? 'verified' : 'outstanding';
    else status = pkg.isPaid ? 'outstanding' : 'unpaid';

    // paidBy on the package wins; the payment record is the fallback source.
    const payerUid = pkg.paidBy || payment?.paidBy || payment?.recordedBy || null;
    const payerName = displayName(payerUid);

    // The date column tracks when the PACKAGE was created, not when it was
    // paid: an unpaid row has no payment record at all, so a payment date left
    // every row that most needs chasing blank and unsortable.
    const createdAt = parseAnyDate(pkg.createdAt);
    const verifiedAt = parseAnyDate(payment?.verifiedAt);
    const amount = payment?.amount ?? (pkg.total != null ? Number(pkg.total) : 0);

    return {
      id: pkg.id,
      paymentId: payment?.id || null,
      recipient: pkg.recipient || 'Unknown recipient',
      trackingNumber: pkg.trackingNumber || '',
      payerName,
      payerUid,
      amount: Number.isFinite(Number(amount)) ? Number(amount) : 0,
      createdAt,
      status,
      verifiedAt,
      verifiedByName: displayName(payment?.verifiedBy),
      // Pre-lowered haystack so the date and status are searchable as text too.
      haystack: [
        pkg.recipient,
        pkg.trackingNumber,
        payerName,
        payerUid,
        STATUS_META[status].label,
        formatDateOnly(createdAt),
        createdAt ? createdAt.toISOString().slice(0, 10) : '',
      ].filter(Boolean).join(' ').toLowerCase(),
    };
  }), [reconcilablePackages, paymentForPackage, displayName]);

  // Totals describe the whole data set, not the current filter, so the cards stay a
  // stable reference point while the operator narrows the table.
  const totals = useMemo(() => {
    let verified = 0;
    let outstanding = 0;
    let unpaid = 0;
    let outstandingValue = 0;
    rows.forEach((row) => {
      if (row.status === 'verified') {
        verified += 1;
      } else {
        outstandingValue += row.amount;
        if (row.status === 'outstanding') outstanding += 1;
        else unpaid += 1;
      }
    });
    return { verified, outstanding, unpaid, outstandingValue };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const term = deferredSearch.trim().toLowerCase();
    const start = startDate ? new Date(`${startDate}T00:00:00`) : null;
    const end = endDate ? new Date(`${endDate}T23:59:59.999`) : null;

    return rows.filter((row) => {
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      // A date bound is a question about the date on screen — when the package
      // was created — so rows with no created date drop out once a bound is set.
      if (start && (!row.createdAt || row.createdAt < start)) return false;
      if (end && (!row.createdAt || row.createdAt > end)) return false;
      if (!term) return true;
      return row.haystack.includes(term);
    });
  }, [rows, deferredSearch, statusFilter, startDate, endDate]);

  const sortedRows = useMemo(() => {
    const dir = sortOrder === 'asc' ? 1 : -1;
    const value = (row) => {
      switch (sortBy) {
        case 'recipient': return row.recipient.toLowerCase();
        case 'payer': return row.payerName ? row.payerName.toLowerCase() : null;
        case 'amount': return row.amount;
        case 'createdAt': return row.createdAt ? row.createdAt.getTime() : null;
        case 'status': return STATUS_META[row.status].rank;
        case 'verifiedAt': return row.verifiedAt ? row.verifiedAt.getTime() : null;
        default: return null;
      }
    };
    return [...filteredRows].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      // Empty cells sort last in both directions - flipping them to the top is
      // never what someone sorting by a date column is looking for.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av === bv) return 0;
      return av > bv ? dir : -dir;
    });
  }, [filteredRows, sortBy, sortOrder]);

  const pageRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage],
  );

  // Keep the page in range when a filter shrinks the list under it.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(sortedRows.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [sortedRows.length, rowsPerPage, page]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      // Text reads best A-Z; numbers, dates and severity read best highest-first.
      setSortOrder(column === 'recipient' || column === 'payer' ? 'asc' : 'desc');
    }
    setPage(0);
  };

  const showToast = useCallback((next) => {
    setToast(next);
    setToastOpen(true);
  }, []);

  const setVerification = useCallback(async (row, verified, { isUndo = false } = {}) => {
    if (!authUser || !row.paymentId) return;
    setProcessingId(row.id);
    try {
      await updateDoc(doc(db, 'payments', row.paymentId), verified
        ? { verified: true, verifiedAt: serverTimestamp(), verifiedBy: authUser.uid }
        : { verified: false, verifiedAt: deleteField(), verifiedBy: deleteField() });

      setPayments(prev => prev.map(p => (p.id === row.paymentId
        ? verified
          ? { ...p, verified: true, verifiedAt: new Date(), verifiedBy: authUser.uid }
          : { ...p, verified: false, verifiedAt: null, verifiedBy: null }
        : p)));

      showToast({
        severity: 'success',
        message: verified
          ? `Payment for ${row.recipient} marked verified.`
          : `Payment for ${row.recipient} marked outstanding.`,
        // Verification is a money decision, so a mis-click is one click to walk back.
        undo: isUndo ? null : () => setVerification(row, !verified, { isUndo: true }),
      });
    } catch (err) {
      console.error('Error updating payment verification:', err);
      showToast({
        severity: 'error',
        message: 'Could not update this payment. Please try again.',
      });
    } finally {
      setProcessingId(null);
    }
  }, [authUser, showToast]);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setStartDate('');
    setEndDate('');
    setPage(0);
  };

  const toggleStatusCard = (status) => {
    setStatusFilter(prev => (prev === status ? 'all' : status));
    setPage(0);
  };

  const filtersActive = Boolean(search || startDate || endDate || statusFilter !== 'all');

  const columns = [
    { id: 'recipient', label: 'Recipient', sortable: true },
    { id: 'payer', label: 'Paid By', sortable: true },
    { id: 'amount', label: 'Amount', sortable: true, align: 'right' },
    { id: 'createdAt', label: 'Created', sortable: true },
    { id: 'status', label: 'Status', sortable: true },
    { id: 'verifiedAt', label: 'Verified At', sortable: true },
    { id: 'action', label: 'Action', sortable: false, align: 'right' },
  ];

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1280, xl: 1600 }, p: { xs: 2, md: 3 }, px: { lg: 4 } }}>
        {/* Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: { xs: 2, md: 3 } }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              component="img"
              src={logo}
              alt=""
              sx={{ height: { xs: 48, md: 64 }, width: 'auto', objectFit: 'contain' }}
            />
            <Box>
              <Typography variant="h5" fontWeight={600} color="text.primary">
                Package Payments
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Every package with its payment status, excluding returned, canceled
                and returning-to-store. Verify received funds or send one back to outstanding.
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
            sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'stretch', sm: 'auto' } }}
          >
            Refresh
          </Button>
        </Stack>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={<Button color="inherit" size="small" onClick={fetchData}>Retry</Button>}
          >
            {error}
          </Alert>
        )}

        {/* Summary. The first three cards double as status filters. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: { xs: 1.5, md: 2 },
            mb: { xs: 2, md: 3 },
          }}
        >
          <StatCard
            label="VERIFIED PAYMENTS"
            value={totals.verified}
            hint={statusFilter === 'verified' ? 'Filtering by verified' : 'Funds confirmed received'}
            accent="#16a34a"
            Icon={VerifiedIcon}
            active={statusFilter === 'verified'}
            onClick={() => toggleStatusCard('verified')}
            loading={loading}
          />
          <StatCard
            label="OUTSTANDING PAYMENTS"
            value={totals.outstanding}
            hint={statusFilter === 'outstanding' ? 'Filtering by outstanding' : 'Recorded, awaiting verification'}
            accent="#b45309"
            Icon={OutstandingIcon}
            active={statusFilter === 'outstanding'}
            onClick={() => toggleStatusCard('outstanding')}
            loading={loading}
          />
          <StatCard
            label="UNPAID PACKAGES"
            value={totals.unpaid}
            hint={statusFilter === 'unpaid' ? 'Filtering by unpaid' : 'No payment recorded yet'}
            accent="#475569"
            Icon={UnpaidIcon}
            active={statusFilter === 'unpaid'}
            onClick={() => toggleStatusCard('unpaid')}
            loading={loading}
          />
          <StatCard
            label="OUTSTANDING VALUE"
            value={formatCurrency(totals.outstandingValue)}
            hint={`Across ${totals.outstanding + totals.unpaid} unverified package${totals.outstanding + totals.unpaid === 1 ? '' : 's'}`}
            accent="#1d4ed8"
            Icon={ValueIcon}
            loading={loading}
          />
        </Box>

        {/* Filters */}
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: { xs: 2, md: 3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
            <TextField
              label="Search"
              placeholder="Recipient, paid by, date, or status..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              size="small"
              fullWidth
              helperText="Matches recipient, payer, tracking number, created date and status"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              size="small"
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="verified">Verified</MenuItem>
              <MenuItem value="outstanding">Outstanding</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
            </TextField>
            <TextField
              label="From date"
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="To date"
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 160 }}
            />
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              disabled={!filtersActive}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear
            </Button>
          </Stack>
        </Paper>

        {/* Table */}
        {!loading && sortedRows.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary" gutterBottom>
              {rows.length === 0
                ? 'No packages found.'
                : 'No packages match your filters.'}
            </Typography>
            {filtersActive && (
              <Button size="small" onClick={clearFilters} startIcon={<ClearIcon />}>
                Clear filters
              </Button>
            )}
          </Paper>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" aria-busy={loading}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <caption style={visuallyHidden}>
                  Package payments, page {page + 1} of {Math.max(1, Math.ceil(sortedRows.length / rowsPerPage))}
                </caption>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 600, whiteSpace: 'nowrap', py: 1.25 } }}>
                    {columns.map((col) => (
                      // sortDirection is what puts aria-sort on the header cell - the
                      // sort label alone does not announce the sorted column.
                      <TableCell
                        key={col.id}
                        align={col.align}
                        sortDirection={col.sortable && sortBy === col.id ? sortOrder : false}
                      >
                        {col.sortable ? (
                          <TableSortLabel
                            active={sortBy === col.id}
                            direction={sortBy === col.id ? sortOrder : 'asc'}
                            onClick={() => handleSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading
                    ? // Skeleton rows hold the table's height so the page does not
                      // jump when the real data lands.
                      Array.from({ length: 8 }).map((_, i) => (
                        <TableRow key={`skeleton-${i}`}>
                          {columns.map((col) => (
                            <TableCell key={col.id} align={col.align} sx={{ py: 1.25 }}>
                              <Skeleton variant="text" width={col.id === 'recipient' ? 160 : 90} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    : pageRows.map((row) => {
                        const isProcessing = processingId === row.id;
                        const canVerify = Boolean(row.paymentId);
                        const createdOn = formatDateTime(row.createdAt);
                        const verifiedOn = formatDateTime(row.verifiedAt);

                        return (
                          <TableRow key={row.id} hover>
                            <TableCell sx={{ py: 1.25 }}>
                              <Link
                                component={RouterLink}
                                to={`/package/${row.id}`}
                                underline="hover"
                                variant="body2"
                                fontWeight={500}
                                sx={{ color: 'text.primary' }}
                              >
                                {row.recipient}
                              </Link>
                              {row.trackingNumber && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {row.trackingNumber}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ py: 1.25 }}>
                              {row.payerName ? (
                                <Typography variant="body2">{row.payerName}</Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                  <Box component="span" sx={visuallyHidden}>No payer recorded</Box>
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell align="right" sx={{ py: 1.25 }}>
                              {/* Tabular figures keep the decimal points aligned down
                                  the column as values change length. */}
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                              >
                                {formatCurrency(row.amount)}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 1.25, whiteSpace: 'nowrap' }}>
                              {createdOn ? (
                                <Typography variant="body2">{createdOn}</Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                  <Box component="span" sx={visuallyHidden}>No created date</Box>
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ py: 1.25 }}>
                              <StatusChip status={row.status} />
                            </TableCell>

                            <TableCell sx={{ py: 1.25, whiteSpace: 'nowrap' }}>
                              {verifiedOn ? (
                                <>
                                  <Typography variant="body2">{verifiedOn}</Typography>
                                  {row.verifiedByName && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      by {row.verifiedByName}
                                    </Typography>
                                  )}
                                </>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                  <Box component="span" sx={visuallyHidden}>Not verified</Box>
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell align="right" sx={{ py: 1.25 }}>
                              {canVerify ? (
                                row.status === 'verified' ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setVerification(row, false)}
                                    disabled={isProcessing}
                                    startIcon={isProcessing
                                      ? <CircularProgress size={14} color="inherit" />
                                      : <OutstandingIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                      whiteSpace: 'nowrap',
                                      color: '#92400e',
                                      borderColor: '#fbbf24',
                                      '&:hover': { borderColor: '#b45309', bgcolor: '#fffbeb' },
                                    }}
                                  >
                                    {isProcessing ? 'Updating' : 'Mark Outstanding'}
                                    <Box component="span" sx={visuallyHidden}>
                                      {` payment for ${row.recipient}`}
                                    </Box>
                                  </Button>
                                ) : (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    disableElevation
                                    onClick={() => setVerification(row, true)}
                                    disabled={isProcessing}
                                    startIcon={isProcessing
                                      ? <CircularProgress size={14} color="inherit" />
                                      : <VerifiedIcon sx={{ fontSize: 16 }} />}
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    {isProcessing ? 'Verifying' : 'Verify'}
                                    <Box component="span" sx={visuallyHidden}>
                                      {` payment for ${row.recipient}`}
                                    </Box>
                                  </Button>
                                )
                              ) : (
                                // Verification writes to the payment record, so a
                                // package with none has nothing to verify yet.
                                <Tooltip title="No payment recorded for this package yet">
                                  <Box component="span" sx={{ display: 'inline-block' }}>
                                    <Button size="small" variant="outlined" disabled sx={{ whiteSpace: 'nowrap' }}>
                                      Verify
                                      <Box component="span" sx={visuallyHidden}>
                                        {` — unavailable, no payment recorded for ${row.recipient}`}
                                      </Box>
                                    </Button>
                                  </Box>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              count={sortedRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Rows per page:"
              getItemAriaLabel={(type) => `Go to ${type} page of packages`}
              sx={{
                // Wrap rather than scroll sideways on a narrow phone, and keep the
                // prev/next buttons at a comfortable touch target.
                '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', rowGap: 1, px: { xs: 1, sm: 2 } },
                '& .MuiTablePagination-actions button': { p: 1.5 },
              }}
            />
          </>
        )}

        {/* One atomic status message: a bare changing number read on its own tells a
            screen reader user nothing about what moved. */}
        <Typography
          role="status"
          aria-atomic="true"
          variant="caption"
          color="text.secondary"
          align="center"
          display="block"
          sx={{ mt: 2 }}
        >
          {loading
            ? 'Loading package payments...'
            : `Showing ${pageRows.length} of ${sortedRows.length} matching packages` +
              ` (${rows.length} total)` +
              (filtersActive ? ', filtered' : '') +
              (excludedCount > 0 ? ` — ${excludedCount} not collectable, excluded` : '')}
        </Typography>
      </Box>

      <Snackbar
        open={toastOpen}
        // An undoable action gets longer than the usual 3-5s so the undo is
        // genuinely reachable before the toast leaves.
        autoHideDuration={toast?.undo ? 8000 : 5000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ transition: { onExited: () => setToast(null) } }}
      >
        <Alert
          severity={toast?.severity || 'success'}
          variant="filled"
          onClose={() => setToastOpen(false)}
          action={toast?.undo ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                const undo = toast.undo;
                setToastOpen(false);
                undo();
              }}
            >
              Undo
            </Button>
          ) : undefined}
        >
          {toast?.message || ''}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserPackageSummary;
